import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ArrowRight } from "lucide-react";
import { API } from "@/constants/roles";
import { MeltoLogo } from "@/components/MeltoLogo";

const formatError = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || String(e)).join(" ");
  return "Login failed. Try again.";
};

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("melto_token", data.token);
      toast.success("Welcome back.");
      navigate(data.role === "admin" ? "/admin/dashboard" : "/dashboard");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const goApply = () => {
    navigate("/");
    setTimeout(() => document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" }), 400);
  };

  const inputCls =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";

  return (
    <div data-testid="login-page" className="relative flex min-h-screen items-center justify-center px-4">
      <div className="bg-grid-cyan bg-grid-fade absolute inset-0" aria-hidden="true" />
      <div className="relative w-full max-w-md">
        <Link
          data-testid="login-back-home-link"
          to="/"
          className="mb-6 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 transition-colors hover:text-cyan-600"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>
        <form
          data-testid="login-form"
          onSubmit={handleLogin}
          className="rounded-3xl border border-slate-200/80 bg-white p-8 shadow-xl shadow-slate-900/5 sm:p-10"
        >
          <MeltoLogo size={48} className="rounded-2xl" testid="login-logo-mark" />
          <h1 data-testid="login-heading" className="mt-6 font-display text-2xl font-bold tracking-tight text-slate-900">
            Log in
          </h1>
          <p className="mt-2 text-sm text-slate-600">Your Melto Studios dashboard — application status and production.</p>

          <div className="mt-8 space-y-5">
            <div>
              <label htmlFor="login-email" className="mb-2 block text-xs font-semibold text-slate-700">
                Email
              </label>
              <input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className={inputCls}
              />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <label htmlFor="login-password" className="mb-2 block text-xs font-semibold text-slate-700">
                  Password
                </label>
                <Link
                  data-testid="login-forgot-password-link"
                  to="/forgot-password"
                  className="text-xs font-medium text-slate-400 transition-colors hover:text-cyan-600"
                >
                  Forgot password?
                </Link>
              </div>
              <input
                id="login-password"
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                className={inputCls}
              />
            </div>
          </div>

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading}
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-cyan-500 py-3.5 text-sm font-semibold text-white shadow-lg shadow-cyan-500/25 transition-all duration-200 hover:bg-cyan-600 disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {loading ? "Logging in..." : "Log in"}
          </button>

          <div className="mt-8 border-t border-slate-100 pt-6 text-center">
            <p className="text-sm text-slate-500">Don't have an account yet?</p>
            <button
              data-testid="login-apply-here-btn"
              type="button"
              onClick={goApply}
              className="group mt-3 inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-slate-800"
            >
              Apply here
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
            <p className="mt-3 text-xs text-slate-400">
              No account needed to apply — your account is created when you submit your application.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
